import { Card } from '../models/card.model';

export interface LadderHierarchyEntry {
    leaderCard: Card;
    childCards: Card[];
}

export function normalizeLane(cardsInLane: Card[]): Card[] {
    const sorted = [...cardsInLane].sort(compareLaneOrder);
    return sorted.map((card, index) => ({
        ...card,
        importance: index + 1
    }));
}

export function recalculateImportance(cardsInLane: Card[], draggedCardId: string, newIndex: number): Card[] {
    const sorted = [...cardsInLane].sort(compareLaneOrder);
    const targetCard = sorted.find(c => c.id === draggedCardId);

    if (!targetCard) return cardsInLane;

    const withoutTarget = sorted.filter(c => c.id !== draggedCardId);
    
    const safeIndex = Math.max(0, Math.min(newIndex, withoutTarget.length));
    
    const reordered = [
        ...withoutTarget.slice(0, safeIndex),
        targetCard,
        ...withoutTarget.slice(safeIndex)
    ];

    // the sequence 'reordered' is correct. Re-map importance blindly.
    return reordered.map((card, index) => ({
        ...card,
        importance: index + 1
    }));
}

export function moveCardBetweenLanes(
    ladderCards: Card[],
    draggedCardId: string,
    targetLevel: number,
    targetIndex: number,
): Card[] {
    const draggedCard = ladderCards.find(card => card.id === draggedCardId);

    if (!draggedCard || draggedCard.priority === null) {
        return ladderCards;
    }

    const sourceLevel = draggedCard.priority;

    if (sourceLevel === targetLevel) {
        return recalculateImportance(
            ladderCards.filter(card => card.priority === sourceLevel),
            draggedCardId,
            targetIndex,
        ).map(card => ({
            ...card,
            priority: targetLevel,
        }));
    }

    const sourceLane = ladderCards
        .filter(card => card.priority === sourceLevel && card.id !== draggedCardId)
        .sort(compareLaneOrder);
    const targetLane = ladderCards
        .filter(card => card.priority === targetLevel && card.id !== draggedCardId)
        .sort(compareLaneOrder);
    const safeTargetIndex = Math.max(0, Math.min(targetIndex, targetLane.length));

    const reorderedTargetLane = [
        ...targetLane.slice(0, safeTargetIndex),
        {
            ...draggedCard,
            priority: targetLevel,
        },
        ...targetLane.slice(safeTargetIndex),
    ].map((card, index) => ({
        ...card,
        importance: index + 1,
    }));

    return [...normalizeLane(sourceLane), ...reorderedTargetLane];
}

export function createSequentialLadderLevels(cardsInLadder: Card[], configuredLevels: number[]): number[] {
    if (cardsInLadder.length === 0) {
        return [...new Set(configuredLevels)].sort((a, b) => a - b);
    }

    const highestConfiguredLevel = configuredLevels.length > 0 ? Math.max(...configuredLevels) : 0;
    const highestUsedLevel = cardsInLadder.reduce((maxLevel, card) => {
        return Math.max(maxLevel, card.priority ?? 1);
    }, 1);
    const highestLevel = Math.max(1, highestConfiguredLevel, highestUsedLevel);

    return Array.from({ length: highestLevel }, (_, index) => index + 1);
}

export function buildHierarchyEntries(
    ladderCards: Card[],
    levels: number[],
): Map<number, LadderHierarchyEntry[]> {
    const normalizedLevels = createSequentialLadderLevels(ladderCards, levels);
    const visibleById = new Map(ladderCards.map((card) => [card.id, card] as const));
    const childrenByParent = new Map<string, Card[]>();

    for (const card of ladderCards) {
        if (!card.parentCardId || !visibleById.has(card.parentCardId)) {
            continue;
        }

        const children = childrenByParent.get(card.parentCardId) ?? [];
        children.push(card);
        childrenByParent.set(card.parentCardId, children);
    }

    const entriesByLevel = new Map<number, LadderHierarchyEntry[]>();

    for (const level of normalizedLevels) {
        const laneCards = [...ladderCards]
            .filter((card) => card.priority === level)
            .sort(compareLaneOrder);

        const entries: LadderHierarchyEntry[] = [];

        for (const card of laneCards) {
            if (card.parentCardId && visibleById.has(card.parentCardId)) {
                continue;
            }

            const childCards = [...(childrenByParent.get(card.id) ?? [])]
                .sort(compareHierarchyChildOrder)
                .map((childCard) => ({
                    ...childCard,
                    priority: level,
                }));

            entries.push({
                leaderCard: {
                    ...card,
                    priority: level,
                },
                childCards,
            });
        }

        entriesByLevel.set(level, entries);
    }

    return entriesByLevel;
}

export function normalizeHierarchicalLadder(ladderCards: Card[], levels: number[]): Card[] {
    const normalizedLevels = createSequentialLadderLevels(ladderCards, levels);
    const entriesByLevel = buildHierarchyEntries(ladderCards, normalizedLevels);

    return flattenHierarchyEntries(entriesByLevel, normalizedLevels);
}

export function moveHierarchyEntry(
    ladderCards: Card[],
    levels: number[],
    movedCardId: string,
    targetLevel: number,
    targetFlatIndex: number,
): Card[] {
    const normalizedLevels = createSequentialLadderLevels(ladderCards, levels);
    const normalizedCards = normalizeHierarchicalLadder(ladderCards, normalizedLevels);
    const entriesByLevel = cloneEntriesMap(buildHierarchyEntries(normalizedCards, normalizedLevels), normalizedLevels);

    let movedEntry: LadderHierarchyEntry | null = null;
    let sourceLevel: number | null = null;

    for (const level of normalizedLevels) {
        const entries = entriesByLevel.get(level) ?? [];
        const entryIndex = entries.findIndex((entry) => entry.leaderCard.id === movedCardId);

        if (entryIndex >= 0) {
            movedEntry = entries.splice(entryIndex, 1)[0] ?? null;
            sourceLevel = level;
            break;
        }
    }

    if (!movedEntry || sourceLevel === null) {
        return normalizedCards;
    }

    const targetEntries = entriesByLevel.get(targetLevel) ?? [];
    const targetEntryIndex = resolveEntryInsertIndex(targetEntries, targetFlatIndex);

    targetEntries.splice(targetEntryIndex, 0, {
        leaderCard: {
            ...movedEntry.leaderCard,
            priority: targetLevel,
        },
        childCards: movedEntry.childCards.map((childCard) => ({
            ...childCard,
            priority: targetLevel,
        })),
    });

    entriesByLevel.set(targetLevel, targetEntries);

    return flattenHierarchyEntries(entriesByLevel, normalizedLevels);
}

export function reorderChildWithinHierarchy(
    ladderCards: Card[],
    levels: number[],
    parentCardId: string,
    childCardId: string,
    targetFlatIndex: number,
): Card[] {
    const normalizedLevels = createSequentialLadderLevels(ladderCards, levels);
    const normalizedCards = normalizeHierarchicalLadder(ladderCards, normalizedLevels);
    const entriesByLevel = cloneEntriesMap(buildHierarchyEntries(normalizedCards, normalizedLevels), normalizedLevels);

    for (const level of normalizedLevels) {
        const entries = entriesByLevel.get(level) ?? [];
        const entryIndex = entries.findIndex((entry) => entry.leaderCard.id === parentCardId);

        if (entryIndex < 0) {
            continue;
        }

        const entry = entries[entryIndex];
        if (!entry) {
            return normalizedCards;
        }

        const childIndex = entry.childCards.findIndex((childCard) => childCard.id === childCardId);
        if (childIndex < 0) {
            return normalizedCards;
        }

        const [draggedChild] = entry.childCards.splice(childIndex, 1);

        if (!draggedChild) {
          return normalizedCards;
        }

        const entryFlatStartIndex = calculateEntryFlatStartIndex(entries, entryIndex);
        const localChildIndex = Math.max(0, targetFlatIndex - entryFlatStartIndex - 1);
        const safeIndex = Math.max(0, Math.min(localChildIndex, entry.childCards.length));

        entry.childCards.splice(safeIndex, 0, draggedChild);
        entries[entryIndex] = entry;
        entriesByLevel.set(level, entries);

        return flattenHierarchyEntries(entriesByLevel, normalizedLevels);
    }

    return normalizedCards;
}

function compareLaneOrder(a: Card, b: Card): number {
    const leftImportance = a.importance ?? Number.MAX_SAFE_INTEGER;
    const rightImportance = b.importance ?? Number.MAX_SAFE_INTEGER;
    const leftCreatedAt = a.createdAt ?? '';
    const rightCreatedAt = b.createdAt ?? '';

    return leftImportance - rightImportance
        || leftCreatedAt.localeCompare(rightCreatedAt)
        || a.id.localeCompare(b.id);
}

function compareHierarchyChildOrder(a: Card, b: Card): number {
    return (a.priority || Number.MAX_SAFE_INTEGER) - (b.priority || Number.MAX_SAFE_INTEGER)
        || (a.importance || Number.MAX_SAFE_INTEGER) - (b.importance || Number.MAX_SAFE_INTEGER)
        || a.createdAt.localeCompare(b.createdAt)
        || a.id.localeCompare(b.id);
}

function flattenHierarchyEntries(
    entriesByLevel: Map<number, LadderHierarchyEntry[]>,
    levels: number[],
): Card[] {
    const flattenedCards: Card[] = [];

    for (const level of levels) {
        let nextImportance = 1;

        for (const entry of entriesByLevel.get(level) ?? []) {
            const members = [entry.leaderCard, ...entry.childCards];

            for (const member of members) {
                flattenedCards.push({
                    ...member,
                    priority: level,
                    importance: nextImportance,
                });
                nextImportance += 1;
            }
        }
    }

    return flattenedCards;
}

function resolveEntryInsertIndex(entries: LadderHierarchyEntry[], targetFlatIndex: number): number {
    if (targetFlatIndex <= 0) {
        return 0;
    }

    let consumedSlots = 0;

    for (let index = 0; index < entries.length; index += 1) {
        if (targetFlatIndex <= consumedSlots) {
            return index;
        }

        consumedSlots += 1 + entries[index].childCards.length;

        if (targetFlatIndex <= consumedSlots) {
            return index + 1;
        }
    }

    return entries.length;
}

function calculateEntryFlatStartIndex(entries: LadderHierarchyEntry[], entryIndex: number): number {
    let flatIndex = 0;

    for (let index = 0; index < entryIndex; index += 1) {
        flatIndex += 1 + entries[index].childCards.length;
    }

    return flatIndex;
}

function cloneEntriesMap(
    entriesByLevel: Map<number, LadderHierarchyEntry[]>,
    levels: number[],
): Map<number, LadderHierarchyEntry[]> {
    const clone = new Map<number, LadderHierarchyEntry[]>();

    for (const level of levels) {
        clone.set(
            level,
            (entriesByLevel.get(level) ?? []).map((entry) => ({
                leaderCard: { ...entry.leaderCard },
                childCards: entry.childCards.map((childCard) => ({ ...childCard })),
            })),
        );
    }

    return clone;
}
